import React, { useEffect, useState } from "react";
import { MdOutlineCalendarToday } from "react-icons/md";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { generateTimeSlots } from "../functions/servicesFunctions";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import useCartStore from "../store/cartStore";
import axiosToken from "../api/axiosClient";

const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export default function ReservationServiceCalendar({
  servicesSelected,
  onContinue,
  disabled = false,
}) {
  console.log({ servicesSelected });
  const { selectedDate } = useCartStore();
  const navigate = useNavigate();
  const [date, setDate] = useState(new Date());
  const [selectedHour, setSelectedHour] = useState(null);
  const [selectedKey, setSelectedKey] = useState(null);
  const [availableHours, setAvailableHours] = useState([]);

  useEffect(() => {
    const fetchSlots = async () => {
      if (!servicesSelected?.durationMinutes || !date) return;
      setSelectedHour(null);
      setSelectedKey(null);
      setAvailableHours([]); // reset

      const formattedDate = formatLocalDate(date); // ex: "2025-10-03"
      // console.log({ formattedDate });

      try {
        const response = await axiosToken.get(
          `/reservation/availability/slots?date=${formattedDate}`,
        );

        if (response?.data?.success && Array.isArray(response.data.data)) {
          let slots = response.data.data;

          // 🕒 Formater en HH:mm
          const formattedSlots = slots.map((iso) => {
            const d = new Date(iso);
            return d.toLocaleTimeString("fr-FR", {
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
              timeZone: "UTC", // ← force UTC
            });
          });

          // 📅 Si aujourd'hui, filtrer les créneaux passés
          const isToday = new Date().toDateString() === date.toDateString();
          const now = new Date();

          const filteredSlots = isToday
            ? formattedSlots.filter((slot) => {
                const [h, m] = slot.split(":").map(Number);
                return (
                  h > now.getHours() ||
                  (h === now.getHours() && m > now.getMinutes())
                );
              })
            : formattedSlots;

          setAvailableHours(filteredSlots);
        } else {
          toast.error("Aucun créneau disponible pour cette date.");
        }
      } catch (error) {
        console.error("Erreur lors du chargement des créneaux :", error);
        toast.error("Impossible de charger les créneaux horaires.");
      }
    };

    fetchSlots();
  }, [date, servicesSelected]);

  // Format final : YYYY-MM-DD HH:mm:ss
  const formatReservationDateTime = (date, hourString) => {
    const [hour, minute] = hourString.split(":").map(Number);
    const fullDate = new Date(date);
    fullDate.setHours(hour);
    fullDate.setMinutes(minute);
    fullDate.setSeconds(0);

    const year = fullDate.getFullYear();
    const month = String(fullDate.getMonth() + 1).padStart(2, "0");
    const day = String(fullDate.getDate()).padStart(2, "0");
    const hours = String(fullDate.getHours()).padStart(2, "0");
    const minutes = String(fullDate.getMinutes()).padStart(2, "0");
    const seconds = "00";

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  const handleContinue = () => {
    if (!date || !selectedHour) return;

    const [hour, minute] = selectedHour.split(":").map(Number);
    const selectedDateTime = new Date(date);
    selectedDateTime.setHours(hour);
    selectedDateTime.setMinutes(minute);
    selectedDateTime.setSeconds(0);

    const now = new Date();
    if (selectedDateTime < now) {
      toast.error("La date sélectionnée est déjà passée.");
      return;
    }

    const formatted = formatReservationDateTime(date, selectedHour);
    if (onContinue) onContinue(formatted);
  };

  if (disabled) {
    return (
      <div className="bg-gray-100 p-6 text-center">
        <div className="text-gray-500 mb-4">
          <svg
            className="w-12 h-12 mx-auto mb-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1}
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
            />
          </svg>
          <p className="font-poppins font-semibold text-[18px] text-[#222]">
            Date déjà sélectionnée
          </p>
          <p className="font-poppins font-bold text-[16px] text-[#111]">
            Date: {selectedDate}
          </p>
          <p className="font-nunito text-[14px] text-[#333] mt-1">
            Vous avez déjà des services dans votre panier avec une date de
            réservation. Veuillez modifier la date directement dans le panier.
          </p>
        </div>
        <button
          onClick={() => navigate("/cart")}
          className="px-6 py-2 bg-[#FF4081] text-white hover:bg-[#FF4081]/90 font-nunito font-semibold text-[14px]"
        >
          Aller au panier
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Sélecteur de date */}
      <div className="pl-6">
        <p className="font-poppins font-semibold text-[18px] text-[#222] mb-2">
          Choisissez la date et l'heure
        </p>
        <h2 className="font-poppins font-medium text-[15px] text-[#444] mb-2">
          Sélectionnez une date
        </h2>
      </div>
      <div className="shadow-lg p-4">
        <Calendar
          minDate={new Date()}
          onClickDay={(value) => setDate(value)}
          value={date}
          className="custom-calendar border-3 shadow p-2"
          locale="fr-FR"
          calendarType="gregory"
        />
      </div>

      {/* Sélecteur d'horaire */}
      <h2 className="font-poppins font-medium text-[15px] text-[#444] mt-6 mb-2">
        Sélectionnez un horaire
      </h2>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
        {availableHours.map((hour, key) => (
          <button
            key={key}
            onClick={() => {
              setSelectedHour(hour);
              setSelectedKey(key);
            }}
            className={`flex items-center justify-center gap-2 border px-3 py-2 font-nunito font-semibold text-[14px]
                        ${
                          selectedHour === hour && selectedKey === key
                            ? "bg-[#FF4081] text-white border-[#FF4081]"
                            : "border-gray-300 text-[#333]"
                        }
            `}
          >
            <MdOutlineCalendarToday size={14} />
            {hour}
          </button>
        ))}
      </div>

      {/* Bouton continuer */}
      <div className="mt-6 text-right">
        <button
          className="bg-[#FF4081] text-white px-5 py-2 disabled:opacity-50 font-nunito font-semibold text-[14px]"
          disabled={!date || !selectedHour}
          onClick={handleContinue}
        >
          Continuer
        </button>
      </div>
    </div>
  );
}
